<script setup lang="ts">
import type { ICredit, IProps } from '~/components/entity/credit'
import type { IResponseData } from '~/types'
import { FeaturePagination } from '~/components/feature/pagination'
import { EntityCreditItem, EntityCreditItemSkeleton } from '~/components/entity/credit'
import { LucideNewspaper } from 'lucide-vue-next'
import { useFetchLocale } from '~/composables/useFetchLocale'

const props = withDefaults(defineProps<IProps>(), {
  hideOrganizationDetails: false,
  hidePagination: false,
  showTypes: false,
  type: 'bank'
})

const emit = defineEmits<{
  loaded: [hasData: boolean]
}>()

const config = useRuntimeConfig()
const baseApiUrl = config.public.baseApiUrl

const route = useRoute()

const page = ref(Number(route.query.page) || 1)

const query = computed(() =>
  new URLSearchParams({
    ...Object.fromEntries(Object.entries(props.queryParams || {}).map(([k, v]) => [k, String(v)])),
    page: String(page.value)
  }).toString()
)

const url = computed(() => `${baseApiUrl}/credits?${query.value}`)

const { data: credits, status } = useFetchLocale<IResponseData<ICredit[]>>(url, {
  key: computed(() => `credits-${page.value}-${query.value}`)
})

const mappedCredits = computed(
  () =>
    credits.value?.data?.map((item) => ({
      ...item,
      ratings: item.ratings?.average_rate ?? null
    })) ?? []
)

watchEffect(() => {
  if (status.value === 'success') {
    emit('loaded', !!credits.value?.data?.length)
  }
})

const fetchCredits = (pageNumber: number) => {
  page.value = pageNumber
}
</script>

<template>
  <div>
    <section v-if="status === 'success'" aria-labelledby="credits">
      <div v-if="mappedCredits.length" class="space-y-3">
        <Heading id="credits" tag="h2" class="sr-only">{{ $t('common.credits') }}</Heading>
        <template v-for="item in mappedCredits" :key="item.id">
          <EntityCreditItem
            :item="item"
            :hide-organization-details="props.hideOrganizationDetails"
            :showTypes="props.showTypes"
            :type="props.type"
            :position="props.position"
          />
        </template>
        <div
          v-if="!props.hidePagination && credits.meta.last_page !== 1"
          class="mt-7 flex justify-end"
        >
          <FeaturePagination :meta="credits.meta" :links="credits.links" @change="fetchCredits" />
        </div>
      </div>
      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('widget.credits.list.no_credits') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else>
      <EntityCreditItemSkeleton
        :count="Number(props.queryParams?.per_page || 3)"
        :hide-organization-details="props.hideOrganizationDetails"
      />
    </section>
  </div>
</template>
